/**
 * Enhanced Jobs API Client
 * Extended job search and management with strategy attribution
 */

import { apiClient } from '../api-client';
import type { 
  Job, 
  JobMatch, 
  SavedJob, 
  ApplicationStatus, 
  JobSearchFilters, 
  SearchResponse, 
  JobFeedback 
} from '../../types';

export class JobsApi {
  /**
   * Search jobs with enhanced attribution
   */
  async search(params: {
    q?: string;
    filters?: JobSearchFilters;
    page?: number;
    pageSize?: number;
    sort?: 'relevance' | 'date' | 'salary';
    engine?: 'auto' | 'jsearch' | 'internal';
    include_attribution?: boolean;
  }): Promise<SearchResponse> {
    const response = await apiClient.post('/jobs/search', {
      include_attribution: true,
      ...params,
    });
    // The API returns SearchResponse directly, not wrapped in a data field
    return response;
  }

  /**
   * Get job matches with strategy attribution
   */
  async getMatches(params: {
    type?: 'recommended' | 'recent' | 'rejected';
    page?: number;
    pageSize?: number;
    user_id: string;
  }): Promise<{
    matches: JobMatch[];
    total_count: number;
    attribution_summary: {
      strategies_used: string[];
      exploration_rate: number;
    };
  }> {
    const response = await apiClient.get('/jobs/matches', { params });
    return response.data;
  }

  /**
   * Get saved jobs with buckets
   */
  async getSavedJobs(params: {
    bucket?: 'to_apply' | 'applied' | 'archived';
    user_id: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    jobs: SavedJob[];
    total_count: number;
    bucket_counts: Record<string, number>;
  }> {
    const response = await apiClient.get('/jobs/saved', { params });
    return response.data;
  }

  /**
   * Get job by ID
   */
  async getJob(jobId: string, options?: {
    include_attribution?: boolean;
    user_id?: string;
  }): Promise<Job> {
    const params = options || {};
    const response = await apiClient.get(`/jobs/${jobId}`, { params });
    return response.data;
  }

  /**
   * Provide feedback on job (save, apply, reject, etc.)
   */
  async provideFeedback(feedback: JobFeedback): Promise<void> {
    await apiClient.post('/jobs/feedback', feedback);
  }

  /**
   * Save job to bucket
   */
  async saveJob(jobId: string, options?: {
    bucket?: 'to_apply' | 'applied' | 'archived';
    notes?: string;
    priority?: 'high' | 'medium' | 'low';
  }): Promise<void> {
    await apiClient.post(`/jobs/${jobId}/save`, options || {});
  }

  /**
   * Update saved job
   */
  async updateSavedJob(jobId: string, updates: {
    bucket?: 'to_apply' | 'applied' | 'archived';
    notes?: string;
    priority?: 'high' | 'medium' | 'low';
  }): Promise<void> {
    await apiClient.patch(`/jobs/${jobId}/saved`, updates);
  }

  /**
   * Remove job from saved
   */
  async unsaveJob(jobId: string): Promise<void> {
    await apiClient.delete(`/jobs/${jobId}/save`);
  }

  /**
   * Bulk update saved jobs
   */
  async bulkUpdateSaved(updates: {
    job_ids: string[];
    bucket?: 'to_apply' | 'applied' | 'archived';
    priority?: 'high' | 'medium' | 'low';
  }): Promise<void> {
    await apiClient.patch('/jobs/saved/bulk', updates);
  }

  /**
   * Reject job (remove from recommendations)
   */
  async rejectJob(jobId: string, reason?: string): Promise<void> {
    await apiClient.post(`/jobs/${jobId}/reject`, {
      reason,
    });
  }

  /**
   * Get rejected jobs
   */
  async getRejectedJobs(params: {
    user_id: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    jobs: Job[];
    total_count: number;
  }> {
    const response = await apiClient.get('/jobs/rejected', { params });
    return response.data;
  }

  /**
   * Undo job rejection
   */
  async unrejectJob(jobId: string): Promise<void> {
    await apiClient.delete(`/jobs/${jobId}/reject`);
  }

  /**
   * Record job view for analytics
   */
  async recordView(jobId: string, metadata?: {
    source?: string;
    strategy_key?: string;
    query_id?: string;
  }): Promise<void> {
    await apiClient.post(`/jobs/${jobId}/view`, metadata || {});
  }

  /**
   * Get similar jobs
   */
  async getSimilarJobs(jobId: string, options?: {
    count?: number;
    include_attribution?: boolean;
  }): Promise<Job[]> {
    const params = options || {};
    const response = await apiClient.get(`/jobs/${jobId}/similar`, { params });
    return response.data;
  }

  /**
   * Get job applications
   */
  async getApplications(params: {
    user_id: string;
    status?: ApplicationStatus['status'];
    page?: number;
    pageSize?: number;
  }): Promise<{
    applications: (ApplicationStatus & { job: Job })[];
    total_count: number;
    status_counts: Record<string, number>;
  }> {
    const response = await apiClient.get('/applications', { params });
    return response.data;
  }

  /**
   * Create job application
   */
  async createApplication(data: {
    job_id: string;
    notes?: string;
    applied_via?: string;
  }): Promise<ApplicationStatus> {
    const response = await apiClient.post('/applications', data);
    return response.data;
  }

  /**
   * Update application status
   */
  async updateApplicationStatus(applicationId: string, updates: {
    status?: ApplicationStatus['status'];
    notes?: string;
    next_step?: string;
    next_step_date?: string;
  }): Promise<ApplicationStatus> {
    const response = await apiClient.patch(`/applications/${applicationId}`, updates);
    return response.data;
  }

  /**
   * Delete application
   */
  async deleteApplication(applicationId: string): Promise<void> {
    await apiClient.delete(`/applications/${applicationId}`);
  }
}

export const jobsApi = new JobsApi();